import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import {
  saveChildbirthOB,
  setAction,
  setFetalHR,
  setContractionsFreq,
  setContractionsDur,
  setVariability,
  setAccelerations,
  setDeceleration,
  setUCStrength,
  setDTR,
  setSize,
  setUpdateInputRows,
} from '../store/childbirthOB.actions'
import { ChildbirthOBConstants } from '../store/childbirthOB.constants'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import Procedure from '../../../../components/procedure/Procedure.component'
import { getChildbirthOBOptionsSelector } from '../../../options/selectors'
import { getChildbirthOBSelector } from '../selectors'
import { IOption } from '../../../../common/common.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialChildbirthOBState } from '../store/childbirthOB.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { goBack } from '../../../../utilities/navigation.util'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'

const ChildbirthOB: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const childbirthOB = useSelector(getChildbirthOBSelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    action,
    fetalHR,
    contractionsFreq,
    contractionsDur,
    variability,
    accelerations,
    deceleration,
    uCStrength,
    dTR,
    size,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
    isEkgImport,
  } = childbirthOB
  const childbirthOBOptions = useSelector(getChildbirthOBOptionsSelector)
  const {
    actionOptions,
    variabilityOptions,
    decelerationOptions,
    accelerationsOptions,
    dtrOptions,
    ucStrengthOptions,
  } = childbirthOBOptions

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const save = () => {
    dispatch(saveChildbirthOB())
  }

  const actionOnChange = (option: IOption) => {
    dispatch(setAction(option))
    save()
  }

  const fetalHROnChange = (values: string[]) => {
    dispatch(setFetalHR(values.join('')))
    save()
  }

  const contractionsFreqOnChange = (values: string[]) => {
    dispatch(setContractionsFreq(values.join('')))
    save()
  }

  const contractionsDurOnChange = (values: string[]) => {
    dispatch(setContractionsDur(values.join('')))
    save()
  }

  const variabilityOnChange = (option: IOption) => {
    dispatch(setVariability(option))
    save()
  }

  const accelerationsOnChange = (option: IOption) => {
    dispatch(setAccelerations(option))
    save()
  }

  const decelerationOnChange = (option: IOption) => {
    dispatch(setDeceleration(option))
    save()
  }

  const uCStrengthOnChange = (option: IOption) => {
    dispatch(setUCStrength(option))
    save()
  }

  const dTROnChange = (option: IOption) => {
    dispatch(setDTR(option))
    save()
  }

  const sizeOnChange = (values: string[]) => {
    dispatch(setSize(values.join('')))
    save()
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(childbirthOB, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialChildbirthOBState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'childbirthOB/SET_TIME', time: time })
              dispatch({ type: 'childbirthOB/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: childbirthOB.key,
                rowActionType: TimelineConstants.SET_CHILDBIRTHOB_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_ACTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('childbirthOB.action'),
            dataTestId: 'Action',
            options: actionOptions,
            action: actionOnChange,
            listHeader: 'Action',
            selectedOption: action,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_FETAL_HR,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('childbirthOB.fetalHR'),
            dataTestId: 'FetalHR',
            elementId: 'FetalHR',
            values: fetalHR.split(''),
            onNumPadClick: fetalHROnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_CONTRACTIONS_FREQ,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('childbirthOB.contractionsFreq'),
            dataTestId: 'ContractionsFreq',
            elementId: 'ContractionsFreq',
            values: contractionsFreq.split(''),
            validCharacters: ['.'],
            onNumPadClick: contractionsFreqOnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_CONTRACTIONS_DUR,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('childbirthOB.contractionsDur'),
            dataTestId: 'ContractionsDur',
            elementId: 'ContractionsDur',
            values: contractionsDur.split(''),
            validCharacters: ['.'],
            onNumPadClick: contractionsDurOnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_VARIABILITY,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('childbirthOB.variability'),
            dataTestId: 'Variability',
            options: variabilityOptions,
            action: variabilityOnChange,
            listHeader: 'Variability',
            selectedOption: variability,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_ACCELERATIONS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('childbirthOB.accelerations'),
            dataTestId: 'Accelerations',
            options: accelerationsOptions,
            action: accelerationsOnChange,
            listHeader: 'Accelerations',
            selectedOption: accelerations,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_DECELERATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('childbirthOB.deceleration'),
            dataTestId: 'Deceleration',
            options: decelerationOptions,
            action: decelerationOnChange,
            listHeader: 'Deceleration',
            selectedOption: deceleration,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_UC_STRENGTH,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('childbirthOB.uCStrength'),
            dataTestId: 'UCStrength',
            options: ucStrengthOptions,
            action: uCStrengthOnChange,
            listHeader: 'UC Strength',
            selectedOption: uCStrength,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_DTR,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('childbirthOB.dTR'),
            dataTestId: 'DTR',
            options: dtrOptions,
            action: dTROnChange,
            listHeader: 'DTR',
            selectedOption: dTR,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ChildbirthOBConstants.SET_SIZE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('childbirthOB.size'),
            elementId: 'Size',
            dataTestId: 'Size',
            values: size.split(''),
            validCharacters: ['.'],
            onNumPadClick: sizeOnChange,
          },
        },
      ],
    },
  ]

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='ChildbirthOBPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Childbirth / OB</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'childbirthOB'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
        />
      </IonContent>
    </IonPage>
  )
}

export default ChildbirthOB
